import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import { isDeadlinePassed } from '../lib/format';
import ApplicationForm from '../components/careers/ApplicationForm';
import { Button } from '../components/ui';
import Icon from '../components/ui/Icon';
import patternLeft from '../assets/careers/pattern-left.webp';

// Optional supporting documents: [files key, storage prefix, form_data field].
// `applicants` has columns only for resume/ID/photo, so these paths ride in
// form_data (jsonb) the same way f1_quran_video_url does — no migration.
// All land in the private applicant-documents bucket; HR opens them through
// signed URLs in the admin panel.
const SUPPORTING_DOC_UPLOADS = [
  ['familyCard', 'family-cards/', 'f1_doc_kk_url'],
  ['driverLicense', 'driver-licenses/', 'f1_doc_sim_url'],
  ['diploma', 'diplomas/', 'f1_doc_ijazah_url'],
  ['healthCert', 'health-certs/', 'f1_doc_sehat_url'],
  ['skck', 'skck/', 'f1_doc_skck_url'],
  ['refLetter', 'reference-letters/', 'f1_doc_referensi_url'],
];

export default function Apply() {
  const { positionId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // Candidate source channel (BRS §9): links like /careers?src=instagram
  // carry ?src through to the apply page; remembered so the source
  // survives navigation between careers and apply.
  const [source] = useState(() => {
    const p = new URLSearchParams(window.location.hash ? window.location.hash.split('?')[1] : window.location.search).get('src')
      || new URLSearchParams(window.location.search).get('src');
    if (p) { try { sessionStorage.setItem('wna_apply_src', p); } catch { /* noop */ } return p; }
    try { return sessionStorage.getItem('wna_apply_src') || 'website'; } catch { return 'website'; }
  });
  const [position, setPosition] = useState(null);
  const [template, setTemplate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Step: 'info' or 'questions'
  const [step, setStep] = useState('info');
  const [basicInfo, setBasicInfo] = useState(null);
  const [basicFiles, setBasicFiles] = useState(null);
  const [answers, setAnswers] = useState({});
  const [invalidQuestionId, setInvalidQuestionId] = useState(null);

  const lang = i18n.language?.split('-')[0] || 'id';
  const hasQuestionnaire = !!(template && questions.length > 0);
  const answeredCount = questions.filter((q) => {
    const v = answers[q.id];
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== null && v !== '';
  }).length;

  useEffect(() => {
    fetchPosition();
  }, [positionId]);

  const fetchPosition = async () => {
    console.log('Fetching position:', positionId);

    // Fetch position
    const { data: posData, error: posError } = await supabase
      .from('job_positions')
      .select('*, branches(name, branch_id)')
      .eq('id', positionId)
      .eq('is_active', true)
      .single();

    console.log('Position result:', { posData, posError });

    if (!posError && posData) {
      setPosition(posData);

      // Fetch template linked to this position
      console.log('Fetching template for position_id:', positionId);
      const { data: templateResults, error: templateError } = await supabase
        .from('questionnaire_templates')
        .select('*, questionnaire_template_questions(question_id, sort_order, weight)')
        .eq('for_position_id', positionId)
        .eq('is_active', true);

      console.log('Template results:', { templateResults, templateError });

      // Use first template if multiple exist
      const templateData = templateResults && templateResults.length > 0 ? templateResults[0] : null;
      console.log('Selected template:', templateData);

      if (templateData) {
        setTemplate(templateData);

        // Fetch the actual questions
        const questionIds = (templateData.questionnaire_template_questions || []).map(q => q.question_id);
        console.log('Question IDs from template:', questionIds);

        if (questionIds.length > 0) {
          const { data: questionsData, error: questionsError } = await supabase
            .from('questions')
            .select('*')
            .in('id', questionIds)
            .eq('is_active', true);

          console.log('Questions result:', { questionsData, questionsError });

          if (questionsData) {
            // Sort by template order
            const sortOrder = {};
            templateData.questionnaire_template_questions.forEach(tq => {
              sortOrder[tq.question_id] = tq.sort_order;
            });
            questionsData.sort((a, b) => (sortOrder[a.id] || 0) - (sortOrder[b.id] || 0));
            setQuestions(questionsData);
          }
        }
      }
    }
    setLoading(false);
  };

  // Handle basic info submission - go to questions if template exists
  const handleBasicInfoSubmit = async (formData, files) => {
    setBasicInfo(formData);
    setBasicFiles(files);

    // If there's a questionnaire, go to questions step
    if (hasQuestionnaire) {
      setStep('questions');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // No questionnaire, submit directly
      await submitApplication(formData, files, {});
    }
  };

  // Handle answer change
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    if (invalidQuestionId === questionId) setInvalidQuestionId(null);
  };

  // An answer counts as present depending on the question type
  const isAnswered = (question) => {
    const value = answers[question.id];
    if (question.question_type === 'multiple_choice') {
      return Array.isArray(value) && value.length > 0;
    }
    if (question.question_type === 'boolean' || question.question_type === 'yes_no') {
      return value === true || value === false;
    }
    return value !== undefined && value !== null && value !== '';
  };

  // Final submission with questionnaire — block on unanswered required questions
  const handleFinalSubmit = async () => {
    const firstMissing = questions.find(q => q.is_required && !isAnswered(q));
    if (firstMissing) {
      setInvalidQuestionId(firstMissing.id);
      const el = document.getElementById(`question-${firstMissing.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setInvalidQuestionId(null);
    await submitApplication(basicInfo, basicFiles, answers);
  };

  const goBackToInfo = () => {
    setStep('info');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Submit everything
  const submitApplication = async (formData, files, questionAnswers) => {
    setSubmitting(true);
    setSubmitStatus(null);

    // Build a storage-safe object key: strip the extension, keep only
    // ASCII word chars, and append the original extension. Indonesian/Arabic
    // filenames with spaces or accents were silently rejected by Storage
    // before this, leaving resume_url null on an otherwise "successful" apply.
    const safeKey = (name, prefix = '') => {
      const dot = name.lastIndexOf('.');
      const ext = dot > -1 ? name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') : '';
      const base = (dot > -1 ? name.slice(0, dot) : name)
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || 'file';
      return `${prefix}${Date.now()}_${base}${ext ? '.' + ext : ''}`;
    };

    try {
      // Upload files to Supabase Storage. Upload errors now THROW so the
      // candidate sees a failure instead of a false success with no documents.
      let resumeUrl = null;
      let idCardUrl = null;
      let photoUrl = null;

      if (files?.resume) {
        const { data, error } = await supabase.storage
          .from('resumes')
          .upload(safeKey(files.resume.name), files.resume, { upsert: false });
        if (error) throw new Error(`resume upload failed: ${error.message}`);
        resumeUrl = data.path;
      }

      if (files?.idCard) {
        const { data, error } = await supabase.storage
          .from('applicant-documents')
          .upload(safeKey(files.idCard.name, 'id-cards/'), files.idCard, { upsert: false });
        if (error) throw new Error(`ID card upload failed: ${error.message}`);
        idCardUrl = data.path;
      }

      if (files?.photo) {
        const { data, error } = await supabase.storage
          .from('applicant-documents')
          .upload(safeKey(files.photo.name, 'photos/'), files.photo, { upsert: false });
        if (error) throw new Error(`photo upload failed: ${error.message}`);
        photoUrl = data.path;
      }

      // FORM 1: optional Quran-recitation video (screening item 2)
      let quranVideoUrl = null;
      if (files?.quranVideo) {
        const { data, error } = await supabase.storage
          .from('applicant-documents')
          .upload(safeKey(files.quranVideo.name, 'quran-videos/'), files.quranVideo, { upsert: false });
        if (error) throw new Error(`video upload failed: ${error.message}`);
        quranVideoUrl = data.path;
      }

      // Optional supporting documents (KK, SIM, ijazah, surat sehat, SKCK,
      // paklaring) — only the ones the candidate actually attached.
      const supportingDocUrls = {};
      for (const [fileKey, prefix, field] of SUPPORTING_DOC_UPLOADS) {
        const doc = files?.[fileKey];
        if (!doc) continue;
        const { data, error } = await supabase.storage
          .from('applicant-documents')
          .upload(safeKey(doc.name, prefix), doc, { upsert: false });
        if (error) throw new Error(`${fileKey} upload failed: ${error.message}`);
        supportingDocUrls[field] = data.path;
      }

      // Generate the id client-side so we never need an INSERT ... RETURNING.
      // Anon has an INSERT policy on applicants but no SELECT policy, so
      // chaining .select() made every submission fail the RLS check and lose
      // the application (with the uploaded files orphaned in storage).
      const applicantId =
        (crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      // FORM 1 answers: every f1_-prefixed field → form_data jsonb
      const formDataJson = Object.fromEntries(
        Object.entries(formData).filter(([k]) => k.startsWith('f1_'))
      );
      if (quranVideoUrl) formDataJson.f1_quran_video_url = quranVideoUrl;
      Object.assign(formDataJson, supportingDocUrls);

      const applicantData = {
        id: applicantId,
        job_position_id: positionId,
        form_data: formDataJson,
        source,
        branch_id: position?.branch_id || null,
        full_name: formData.fullName,
        phone: formData.phone,
        email: formData.email || null,
        date_of_birth: formData.dateOfBirth || null,
        gender: formData.gender || null,
        marital_status: formData.maritalStatus || null,
        current_city: formData.currentCity || null,
        current_address: formData.currentAddress || null,
        province: formData.province || null,
        available_start_date: formData.availableStartDate || null,
        willing_to_relocate: formData.willingToRelocate || false,
        willing_for_dorm: formData.willingForDorm || false,
        preferred_shift: formData.preferredShift || null,
        resume_url: resumeUrl,
        id_card_url: idCardUrl,
        photo_url: photoUrl,
        status: 'submitted',
      };

      let { error: insertError } = await supabase
        .from('applicants')
        .insert(applicantData);

      // pre-migration DB (no form_data/source columns yet): don't lose the
      // application — retry with the legacy shape
      if (insertError && /form_data|source|screening/.test(insertError.message || '')) {
        const { form_data: _fd, source: _src, ...legacy } = applicantData;
        ({ error: insertError } = await supabase.from('applicants').insert(legacy));
      }

      if (insertError) throw insertError;

      // Save questionnaire responses (errors surfaced, not swallowed)
      if (Object.keys(questionAnswers).length > 0) {
        const responses = Object.entries(questionAnswers).map(([questionId, answer]) => {
          const question = questions.find(q => q.id === questionId);
          return {
            applicant_id: applicantId,
            question_id: questionId,
            template_id: template?.id || null,
            response_text: typeof answer === 'string' ? answer : null,
            response_numeric: typeof answer === 'number' ? answer : null,
            response_boolean: typeof answer === 'boolean' ? answer : null,
            response_choice: question?.question_type === 'single_choice' ? answer : null,
            response_choices: question?.question_type === 'multiple_choice' ? answer : null,
          };
        });

        const { error: respError } = await supabase.from('applicant_responses').insert(responses);
        if (respError) throw respError;
      }

      setSubmitStatus('success');
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const getLocalizedField = (obj, field) => {
    if (!obj) return '';
    return obj[`${field}_${lang}`] || obj[`${field}_id`] || obj[`${field}_en`] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  if (!position) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('apply.notFoundTitle')}</h2>
        <p className="text-gray-500 mb-6 max-w-sm">{t('apply.notFoundMessage')}</p>
        <Button to="/careers">{t('apply.backToCareers')}</Button>
      </div>
    );
  }

  // Hiding the posting on the careers page is not enough on its own: a direct
  // apply link, shared or bookmarked, still reaches this page. Refuse the form
  // here too, so the deadline means the same thing however the candidate
  // arrived. HR keeps control of the posting's status either way.
  if (isDeadlinePassed(position.apply_deadline)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="calendar" className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t('apply.closedTitle', 'Pendaftaran sudah ditutup')}
        </h2>
        <p className="text-gray-500 mb-6 max-w-sm">
          {t('apply.closedMessage', 'Batas waktu pendaftaran untuk posisi ini sudah lewat. Silakan lihat lowongan lain yang masih terbuka.')}
        </p>
        <Button to="/careers">{t('apply.backToCareers')}</Button>
      </div>
    );
  }

  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('apply.successTitle')}</h2>
          <p className="text-gray-600 mb-2">
            {t('apply.successBody', { position: position.title })}
          </p>
          <p className="text-gray-500 text-sm mb-6">{t('apply.successNext')}</p>
          <Button to="/careers">{t('apply.backToCareers')}</Button>
        </div>
      </div>
    );
  }

  // Get localized question text
  const getLocalizedQuestion = (question) => {
    return question[`question_${lang}`] || question.question_id || question.question_en || '';
  };

  // Get localized options
  const getLocalizedOptions = (question) => {
    return question[`options_${lang}`] || question.options_id || question.options_en || [];
  };

  // Render question input based on type
  const renderQuestionInput = (question) => {
    const value = answers[question.id] || '';
    const options = getLocalizedOptions(question);

    switch (question.question_type) {
      case 'free_text':
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('apply.answerPlaceholder')}
          />
        );

      case 'single_choice':
        return (
          <div className="space-y-2">
            {(options || []).map((option, idx) => {
              const picked = value === option;
              return (
                <label
                  key={idx}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 p-3.5 transition-colors ${
                    picked
                      ? 'border-primary-500 bg-primary-50/60'
                      : 'border-gray-200 hover:border-primary-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question_${question.id}`}
                    value={option}
                    checked={picked}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    className="h-4 w-4 shrink-0 text-primary-600 focus:ring-primary-500"
                  />
                  <span className={picked ? 'font-medium text-gray-900' : 'text-gray-700'}>{option}</span>
                </label>
              );
            })}
          </div>
        );

      case 'multiple_choice':
        const selectedChoices = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {(options || []).map((option, idx) => (
              <label key={idx} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={selectedChoices.includes(option)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleAnswerChange(question.id, [...selectedChoices, option]);
                    } else {
                      handleAnswerChange(question.id, selectedChoices.filter(c => c !== option));
                    }
                  }}
                  className="w-4 h-4 text-primary-600"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case 'boolean':
      case 'yes_no':
        return (
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => handleAnswerChange(question.id, true)}
              className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors ${
                value === true
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('common.yes')}
            </button>
            <button
              type="button"
              onClick={() => handleAnswerChange(question.id, false)}
              className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors ${
                value === false
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('common.no')}
            </button>
          </div>
        );

      case 'scale_5':
      case 'rating':
        return (
          <div>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => handleAnswerChange(question.id, num)}
                  aria-pressed={value === num}
                  className={`h-12 w-12 rounded-xl border-2 font-semibold transition-all ${
                    value === num
                      ? 'scale-105 border-primary-500 bg-primary-500 text-white shadow-sm'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            {/* A bare 1-5 row does not say which end is which */}
            <div className="mt-2 flex max-w-[19rem] justify-between text-xs text-gray-400">
              <span>{t('apply.scaleLow', 'Sangat tidak setuju')}</span>
              <span>{t('apply.scaleHigh', 'Sangat setuju')}</span>
            </div>
          </div>
        );

      case 'scale_10':
        return (
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleAnswerChange(question.id, num)}
                className={`w-10 h-10 rounded-lg border-2 font-semibold text-sm ${
                  value === num
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-primary-300'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        );

      case 'numeric_rating':
      case 'numeric':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value === '' ? '' : parseFloat(e.target.value))}
            className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            min="0"
            max={question.max_score || 100}
            placeholder="0"
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        );
    }
  };

  return (
    <div className="relative overflow-hidden bg-[#fdf6ec] py-12 md:py-16">
      {/* Same batik as the careers page, so the form does not feel like a
          plain white detour off the end of it. */}
      <img
        src={patternLeft}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -left-10 top-0 z-0 hidden h-[560px] w-auto opacity-80 lg:block"
      />
      <img
        src={patternLeft}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 bottom-0 z-0 hidden h-[520px] w-auto scale-x-[-1] opacity-70 lg:block"
      />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Link */}
        <Link to="/careers" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('common.back')}
        </Link>

        {/* Position Info */}
        <div className="bg-primary-50 rounded-xl p-6 mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{position.title}</h1>
          {position.branches && (
            <p className="text-gray-600">
              {position.branches.name}
            </p>
          )}
          {getLocalizedField(position, 'description') && (
            <p className="text-gray-600 mt-4">{getLocalizedField(position, 'description')}</p>
          )}
        </div>

        {/* Progress Steps */}
        {hasQuestionnaire && (
          <div className="flex items-center gap-3 sm:gap-4 mb-8">
            {/* Step 1 — always complete once we're on the questions step */}
            <div className={`flex items-center gap-2 ${step === 'info' ? 'text-primary-600 font-semibold' : 'text-green-600 font-medium'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'info' ? 'bg-primary-600 text-white' : 'bg-green-100 text-green-600'}`}>
                {step === 'info' ? (
                  '1'
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-sm sm:text-base">{t('apply.stepPersonalInfo')}</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 rounded">
              <div className={`h-full bg-primary-600 rounded transition-all ${step === 'questions' ? 'w-full' : 'w-0'}`}></div>
            </div>
            <div className={`flex items-center gap-2 ${step === 'questions' ? 'text-primary-600 font-semibold' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'questions' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>2</div>
              <span className="text-sm sm:text-base">{t('apply.stepQuestionnaire')}</span>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
            {t('apply.error')}
          </div>
        )}

        {/* Step 1: Basic Info — kept mounted so react-hook-form values and the
            selected files survive a round-trip to the questionnaire and back. */}
        <div className={step === 'info' ? '' : 'hidden'}>
          <ApplicationForm
            onSubmit={handleBasicInfoSubmit}
            submitting={submitting}
            hasQuestionnaire={hasQuestionnaire}
            // Which documents are wajib is set per posting by HR; null (any
            // posting saved before the column existed) means the defaults.
            requiredDocKeys={position?.required_documents}
            // Same idea for the shift dropdown: a night-shift-only posting
            // should not offer morning. null = all four, as before.
            availableShifts={position?.available_shifts}
          />
        </div>

        {/* Step 2: Questionnaire */}
        {step === 'questions' && (
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm md:p-8">
            <div className="mb-6">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                    <Icon name="clipboard" className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="font-serif text-xl font-bold text-gray-900">
                      {t('apply.questionnaireHeading')}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {t('apply.questionsCount', { total: questions.length })}
                    </p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-primary-600">
                  {answeredCount} / {questions.length}
                </span>
              </div>
              {/* Progress across the questionnaire. A long list of questions
                  with no sense of how much is left is the point most people
                  abandon a form. */}
              <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all duration-500"
                  style={{ width: `${questions.length ? (answeredCount / questions.length) * 100 : 0}%` }}
                />
              </div>
            </div>

            {template?.time_limit_minutes && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800 text-sm">
                  ⏱️ {t('apply.timeLimit', { minutes: template.time_limit_minutes })}
                </p>
              </div>
            )}

            <div className="space-y-8">
              {questions.map((question, idx) => {
                const isInvalid = invalidQuestionId === question.id;
                return (
                  <div
                    key={question.id}
                    id={`question-${question.id}`}
                    className={`pb-6 border-b border-gray-100 last:border-0 ${isInvalid ? 'ring-2 ring-red-300 rounded-lg p-4 -mx-1 bg-red-50/40' : ''}`}
                  >
                    <div className="mb-3 flex gap-3">
                      <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
                        isAnswered(question) ? 'bg-primary-500 text-white' : 'bg-primary-50 text-primary-600'
                      }`}>
                        {isAnswered(question) ? '✓' : idx + 1}
                      </span>
                      <p className="font-medium text-gray-900">
                        {getLocalizedQuestion(question)}
                        {question.is_required && <span className="ml-1 text-red-500">*</span>}
                      </p>
                    </div>
                    {renderQuestionInput(question)}
                    {isInvalid && (
                      <p className="mt-2 text-sm text-red-600" role="alert">
                        {t('apply.answerRequired')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-4 mt-8">
              <button
                type="button"
                onClick={goBackToInfo}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <svg className="w-5 h-5 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                {t('common.back')}
              </button>
              <Button
                type="button"
                onClick={handleFinalSubmit}
                disabled={submitting}
                className="flex-1 disabled:opacity-50"
              >
                {submitting ? t('apply.submitting') : t('apply.submit')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
